import { useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import moment from "moment";
import { z } from "zod";

import { usePatientData } from "features/patients/hooks/usePatientData";
import { useStaffInfo } from "features/practice/hooks/useStaffInfo";
import { useCreateLetter } from "features/templates/hooks/useCreateLetter";
import { onInvalidSubmit } from "utils/reportFormErrors";
import { validateEircode } from "utils/validators";

import style from "./CervicalCheck.module.css";
import { CervicalScreeningTestInformation } from "./components/CervicalScreeningTestInformation";
import { DoctorDetails } from "./components/DoctorDetails";
import { PatientDetails } from "./components/PatientDetails";
import { RelevantClinicalDetails } from "./components/RelevantClinicalDetails";
import { SampleTakersDetails } from "./components/SampletakersDetails";
import { ScreeningAndThreatmentHistory } from "./components/ScreeningAndThreatmentHistory";

const cervicalCheckFormSchema = z.object({
  personalPublicServiceNumber: z
    .string()
    .min(1, { message: "Please enter personal public service number." }),
  cspId: z.string().min(1, { message: "Please enter csp id." }),
  hospitalNumber: z
    .string()
    .min(1, { message: "Please enter hospital number." }),
  contactNumber: z.string().min(1, { message: "Please enter contact number." }),
  firstName: z.string().min(1, { message: "Please enter first name." }),
  middleName: z.string().optional(),
  surname: z.string().min(1, { message: "Please enter surname." }),
  surnameAtBirth: z
    .string()
    .min(1, { message: "Please enter surname at birth." }),
  dateOfBirth: z
    .string()
    .min(1, "Date of Birth is required")
    .refine(
      (val) => {
        return moment(val, ["DD-MM-YYYY", moment.ISO_8601]).isValid();
      },
      {
        message: "Invalid Date of Birth",
      }
    ),
  mothersMaidenName: z
    .string()
    .min(1, { message: "Please enter mothers maiden name." }),
  address: z.string().min(1, { message: "Please enter address." }),
  eircode: z
    .string()
    .min(1, { message: "Please enter eircode." })
    .refine((val) => validateEircode(val).isValid, {
      message: (val) => validateEircode(val).error || "Invalid Eircode format",
    }),

  medicalCouncilRegistrationNumber: z.string(),
  clinicCode: z.enum(["CLIN", "COLP", "GYN", "PPCC", "STI", "CNC"]),
  contractedDoctorName: z
    .string()
    .min(1, { message: "Please enter contracted doctor name." }),
  doctorAddress: z.string().min(1, { message: "Please enter doctor address." }),
  doctorContactNumber: z
    .string()
    .min(1, { message: "Please enter doctor contact number." }),

  mcrnOrNmbi: z.string().min(1, { message: "Please enter mcrn or nmbi." }),
  sampletakerName: z
    .string()
    .min(1, { message: "Please enter sampletaker name." }),

  dateOfTest: z.string().min(1, "Date of Birth is required"),
  sampleSite: z.enum(["cervix", "vault"]),
  lmp: z.string().min(1, "Date of Birth is required"),
  ocpHormonesHrt: z.boolean(),
  prePostTransplant: z.boolean(),
  postCoitalBleeding: z.boolean(),
  iucd: z.boolean(),
  dialysis: z.boolean(),
  postMenopausalBleeding: z.boolean(),
  postMenopausal: z.boolean(),
  hivPositive: z.boolean(),
  subTotalHysterectomy: z.boolean(),
  totalHysterectomy: z.boolean(),
  screeningAndThreatmentHistory: z.string(),
});

export const CervicalCheckForm = ({ initialData }) => {
  const savedId = useRef(null);
  const today = dayjs(new Date().setMinutes(0));
  const { data: patient } = usePatientData();
  const { data: doctor } = useStaffInfo();

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");
  const form = useForm({
    resolver: zodResolver(cervicalCheckFormSchema),
    defaultValues: {
      personalPublicServiceNumber:
        initialData?.personalPublicServiceNumber ||
        patient.medicalCardNumber ||
        "",
      cspId: initialData?.cspId || "",
      hospitalNumber: initialData?.hospitalNumber || "",
      contactNumber: initialData?.contactNumber || patient.mobilePhone || "",
      firstName: initialData?.firstName || patient.firstName || "",
      middleName: initialData?.middleName || patient.middleNames || "",
      surname: initialData?.surname || patient.surname || "",
      surnameAtBirth: initialData?.surnameAtBirth || "",
      dateOfBirth: initialData?.dateOfBirth || patient.dateOfBirth || "",
      mothersMaidenName: initialData?.mothersMaidenName || patient.mothersMaidenName || "",
      address:
        initialData?.address ||
        `${patient.address.addressLine1 || ""} ${
          patient.address.addressLine2 || ""
        } ${patient.address.addressLine3 || ""} ${patient.address.city || ""} ${
          patient.address.county || ""
        } ${patient.address.country || ""}` ||
        "",
      eircode: initialData?.eircode || patient.address.postCode || "",

      medicalCouncilRegistrationNumber:
        initialData?.medicalCouncilRegistrationNumber ||
        doctor.staffMember.professionalMedicalNumber ||
        "",
      clinicCode: initialData?.clinicCode || "",
      contractedDoctorName:
        initialData?.contractedDoctorName ||
        `${doctor.staffMember.mainRole || ""} ${
          doctor.staffMember.firstName || ""
        } ${doctor.staffMember.lastName || ""}` ||
        "",
      doctorAddress:
        initialData?.doctorAddress || doctor.staffMember.homeNumber || "",
      doctorContactNumber:
        initialData?.doctorContactNumber || doctor.staffMember.mobile || "",

      mcrnOrNmbi:
        initialData?.mcrnOrNmbi ||
        doctor.staffMember.professionalMedicalNumber ||
        "",
      sampletakerName:
        initialData?.sampletakerName ||
        `${doctor.staffMember.mainRole || ""} ${
          doctor.staffMember.firstName || ""
        } ${doctor.staffMember.lastName || ""}` ||
        "",

      dateOfTest: initialData?.dateOfTest || "",
      sampleSite: initialData?.sampleSite || "",
      lmp: initialData?.lmp || "",
      ocpHormonesHrt: initialData?.ocpHormonesHrt || false,
      prePostTransplant: initialData?.prePostTransplant || false,
      postCoitalBleeding: initialData?.postCoitalBleeding || false,
      iucd: initialData?.iucd || false,
      dialysis: initialData?.dialysis || false,
      postMenopausalBleeding: initialData?.postMenopausalBleeding || false,
      postMenopausal: initialData?.postMenopausal || false,
      hivPositive: initialData?.hivPositive || false,
      subTotalHysterectomy: initialData?.subTotalHysterectomy || false,
      totalHysterectomy: initialData?.totalHysterectomy || false,
      screeningAndThreatmentHistory:
        initialData?.screeningAndThreatmentHistory || "",
    },
  });
  const createLetter = useCreateLetter();

  const handleFormSubmit = async (values) => {
    try {
      const res = await createLetter.mutateAsync({
        id: initialData?.id || savedId.current,
        patientId: patient.id,
        tenant: tenantVal,
        text: values.letter,
        name: values.title,
        letterType: values.type,
      });

      savedId.current = res.id;
    } catch (error) {
      console.error("Submission error:", error);
    }
  };

  return (
    <div style={{ width: "100%" }}>
      <h2
        style={{
          borderBottom: "1px solid var(--gray-200)",
          paddingBottom: "12px",
          marginBottom: "16px",
          fontSize: "20px",
          lineHeight: "32px",
        }}
      >
        Cervical Check
      </h2>
      <FormProvider {...form}>
        <form
          className={style.addLetterForm}
          onSubmit={form.handleSubmit(handleFormSubmit, onInvalidSubmit("CervicalCheck"))}
        >
          <PatientDetails form={form} today={today} />
          <DoctorDetails form={form} />
          <SampleTakersDetails form={form} />
          <CervicalScreeningTestInformation form={form} today={today} />
          <RelevantClinicalDetails form={form} today={today} />
          <ScreeningAndThreatmentHistory form={form} />
          <div className={style.bottomSection}>
            <div>
              <button
                className={style.sendButton}
                onClick={form.handleSubmit(handleFormSubmit, onInvalidSubmit("CervicalCheck"))}
              >
                Save
              </button>
            </div>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};
